import React from 'react';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { addHourToStartTime, getSelectedDate, getSelectedResource, getSelectedStartTime } from './functions/addJobOrActivityUtils';
import { SCHEDULING_COLOR_OPTIONS } from './schedulingConstants';

// Rename the name of the function:
const AddActivityForm: React.FC<{ 
    viewKey?: string, 
    linkedItem?: { [key: string]: number }
}> = ({ viewKey, linkedItem }) => {

    const apiObject = 'activity';

    // Get the selected date, start time and resource
    const selectedDate = getSelectedDate(linkedItem);
    const selectedStartTime = getSelectedStartTime(linkedItem);
    const selectedResource = getSelectedResource(linkedItem);

    // Configure the activity group fields
    const fields: FieldType[] = [
        { 
            type: 'dropdown', 
            name: 'activity_group_time_type', 
            label: 'timerow.general.type_label', 
            apiRequest: [{
                endpoint: 'get_non_productive_timetype_list',
                object: 'non_productive_timetype',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'timerow.general.type_disabled_selected',
            allowNoneOption: false,
            showSearch: false,
        },
        {
            type: 'textarea',
            name: 'activity_group_note',
            label: 'timerow.general.note_label',
            rows: 3,
            isNote: true,
            placeholder: 'timerow.general.note_placeholder',
        },
        // {
        //     type: 'header',
        // },
        {
            type: 'date',
            name: 'activity_group_start_date',
            label: 'timerow.general.start_date_label',
            ...(selectedDate ? { defaultValue: selectedDate } : {}),
            width: '40%',
            group: 'field-row',
        },
        // {
        //     type: 'date',
        //     name: 'activity_group_end_date',
        //     label: 'timerow.general.end_date_label',
        //     width: '50%',
        //     group: 'field-row',
        // },
        {
            type: 'time',
            name: 'activity_group_start_time',
            label: 'timerow.general.start_time_label',
            ...(selectedStartTime ? { defaultValue: selectedStartTime } : {}),
            width: '30%',
            group: 'field-row',
        },
        {
            type: 'time',
            name: 'activity_group_end_time',
            label: 'timerow.general.end_time_label',
            ...(selectedStartTime ? { defaultValue: addHourToStartTime(selectedStartTime) } : {}),
            width: '30%',
            group: 'field-row',
        },
        {
            type: 'dropdown',
            name: 'activity_group_frequency',
            label: 'timerow.general.frequency_label',
            hardcodedOptions: [
                { value: 'not_repeated', name: 'timerow.general.frequency_not_repeated' },
                { value: 'daily', name: 'timerow.general.frequency_daily' },
                { value: 'weekly', name: 'timerow.general.frequency_weekly' },
                { value: 'monthly', name: 'timerow.general.frequency_monthly' },
                { value: 'yearly', name: 'timerow.general.frequency_yearly' },
            ],
            defaultValue: { value: 'not_repeated' },
            allowNoneOption: false,
            showSearch: false,
        },
        {
            name: 'activity_group_weekdays',
            label: 'timerow.general.select_weekdays_label',
            tooltipText: 'timerow.general.select_weekdays_tooltipText',
            type: 'multi-tabs',
            fullWidth: true,
            dependentOn: { fieldName: 'activity_group_frequency', value: 'daily' },
            defaultValue: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            hardcodedOptions: [
                { value: "monday", name: "date_time.days.short.monday" },
                { value: "tuesday", name: "date_time.days.short.tuesday" },
                { value: "wednesday", name: "date_time.days.short.wednesday" },
                { value: "thursday", name: "date_time.days.short.thursday" },
                { value: "friday", name: "date_time.days.short.friday" },
                { value: "saturday", name: "date_time.days.short.saturday" },
                { value: "sunday", name: "date_time.days.short.sunday" },
            ],
        },
        {
            type: 'dropdown',
            name: 'activity_group_end_type',
            label: 'timerow.general.end_type_label',
            hardcodedOptions: [
                { value: 'never', name: 'timerow.general.end_type_never' },
                { value: 'on_date', name: 'timerow.general.end_type_on_date' },
                { value: 'after_count', name: 'timerow.general.end_type_after_count' },
            ],
            defaultValue: { value: 'never' },
            allowNoneOption: false,
            showSearch: false,
            dependentOn: [
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', notValue: ['on_date', 'after_count'] },
            ],
        },
        {
            // Same field as above, but taking half the row once a date or count is revealed next to it
            type: 'dropdown',
            name: 'activity_group_end_type',
            label: 'timerow.general.end_type_label',
            hardcodedOptions: [
                { value: 'never', name: 'timerow.general.end_type_never' },
                { value: 'on_date', name: 'timerow.general.end_type_on_date' },
                { value: 'after_count', name: 'timerow.general.end_type_after_count' },
            ],
            allowNoneOption: false,
            showSearch: false,
            width: '50%',
            group: 'field-row-end',
            dependentOn: [
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', value: ['on_date', 'after_count'] },
            ],
        },
        {
            type: 'date',
            name: 'activity_group_end_date',
            label: 'timerow.general.end_date_label',
            width: '50%',
            group: 'field-row-end',
            dependentOn: [
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', value: 'on_date' },
            ],
        },
        {
            type: 'number',
            name: 'activity_group_occurrence_count',
            label: 'timerow.general.occurrence_count_label',
            placeholder: 'timerow.general.occurrence_count_placeholder',
            min: 1,
            width: '50%',
            group: 'field-row-end',
            dependentOn: [
                { fieldName: 'activity_group_frequency', notValue: 'not_repeated' },
                { fieldName: 'activity_group_end_type', value: 'after_count' },
            ],
        },
        {
            type: 'multiselect',
            name: 'activity_group_assignees',
            label: 'timerow.general.assignees_label',
            apiObject: 'user',
            apiRequest: [{
                endpoint: `get_user_list`,
                object: 'user',
                params: { is_active: true }
            }],
            disabled_selected: 'timerow.general.assignees_disabled_selected',
            ...(selectedResource ? { defaultValue: selectedResource } : {}),
            backendField: 'user_hash',
            allowNoneOption: false,
            optionFormat: (option) => option.full_name,
            selectionFormat: 'full_name',
            params: {
                is_active: true,
            },
        },       
        {
            name: 'activity_group_custom_color',
            label: 'timerow.general.color_label',
            type: 'tabs',
            showIconOnly: true,
            customClass: "label",
            options: SCHEDULING_COLOR_OPTIONS,
            defaultValueFromField: { fieldName: 'activity_group_time_type', dropdownObject: 'non_productive_timetype' },
        },
    ];

    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields} 
            linkedItem={linkedItem}      
        />
    );
};

export default AddActivityForm;